// Board — the kanban page over the caller's app_request rows (formerly the
// "My Builds" sidebar popover). Cross-project by construction: it reads
// /api/my_app_requests, which ignores the active project, so a build filed in
// project A stays visible while viewing project B.
//
// Columns map DB statuses 1:1 except Running, which folds in the seconds-long
// 'claimed' handshake of the worker contract. Drag-and-drop covers exactly the
// user-controlled moves (BOARD_MOVES, mirrored by the backend's PATCH
// /api/app_requests/:id/status): todo ⇄ queued, todo|failed → done (close a
// card whose work happened outside the fleet queue), failed|cancelled →
// queued. In-flight cards are not draggable — a live agent owns those; the
// stale-cancel escape hatch and dismiss cover them.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AppWindow,
  Bot,
  CircleDashed,
  CircleUser,
  ClipboardList,
  Copy,
  CornerDownRight,
  History,
  Link2,
  Loader2,
  MessageSquare,
  Paperclip,
  Plus,
  Trash2,
  X,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useConfirm } from "@/components/ui/confirm-dialog";
import AppRequestDialog from "@/components/AppRequestDialog";
import {
  fetchMyRequests,
  cancelMyRequest,
  dismissMyRequest,
  dismissMyTerminalRequests,
  editRequest,
  moveRequest,
  requestAttachmentUrl,
  fetchRequest,
  addRequestComment,
  fetchRequestLinks,
  linkRequest,
  unlinkRequest,
  LINK_KINDS,
} from "@/services/appRequests";
import { subscribeAlertsChange } from "@/services/alerts";
import { listApps, listAgents } from "@/services/apps";
import AttachmentPicker, { prepareAttachments } from "@/components/board/AttachmentPicker";
import { useProject } from "@/contexts/ProjectContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  BOARD_COLUMNS,
  BOARD_MOVES,
  REQUEST_KIND_META,
  STATUS_META,
  OPEN_STATUSES,
  TERMINAL_STATUSES,
  timeAgo,
  logStamp,
  elapsed,
  queueHint,
  isStale,
  fmtTokens,
  fmtMs,
  btnPrimary,
  btnGhost,
  fieldCls,
  sectionLabel,
} from "@/components/board/requestMeta";

const POLL_MS = 12000;
const TICK_MS = 1000;
const DND_TYPE = "application/x-cortex-request";

// Worker change-reports are markdown by contract — rendered as such (a report
// whose download link shows as raw brackets is how "i havent link" happens).
// Mirrors Feed's MD_COMPONENTS, sized for the task window.
const MD_COMPONENTS = {
  a: (p) => <a {...p} target="_blank" rel="noreferrer" className="text-nav-active underline underline-offset-2" />,
  p: (p) => <p {...p} className="text-xs text-page-text leading-relaxed my-1" />,
  ul: (p) => <ul {...p} className="list-disc pl-4 my-1 space-y-0.5 text-xs text-page-text" />,
  ol: (p) => <ol {...p} className="list-decimal pl-4 my-1 space-y-0.5 text-xs text-page-text" />,
  li: (p) => <li {...p} className="text-xs text-page-text" />,
  h1: (p) => <h3 {...p} className="text-xs font-semibold text-page-title mt-2 mb-1" />,
  h2: (p) => <h3 {...p} className="text-xs font-semibold text-page-title mt-2 mb-1" />,
  h3: (p) => <h4 {...p} className="text-xs font-semibold text-page-title mt-2 mb-1" />,
  code: (p) => <code {...p} className="text-[11px] bg-card-bg rounded px-1 py-0.5" />,
  blockquote: (p) => <blockquote {...p} className="border-l-2 border-card-border pl-2 text-page-muted my-1" />,
  table: (p) => <div className="overflow-x-auto my-1"><table {...p} className="text-xs border-collapse" /></div>,
  th: (p) => <th {...p} className="border border-card-border px-2 py-0.5 text-left font-semibold" />,
  td: (p) => <td {...p} className="border border-card-border px-2 py-0.5" />,
};

// Comments stay plain text (their line breaks matter), but URLs and markdown
// links become clickable — an executor's "download: [x](/api/report_file/…)"
// must be a link, not brackets.
const COMMENT_LINK_RE = /\[([^\]]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s)]+|\/api\/[A-Za-z0-9_\-./]+)/g;
function linkifyText(text) {
  const s = String(text || "");
  const out = [];
  let last = 0;
  const re = new RegExp(COMMENT_LINK_RE);
  let m;
  while ((m = re.exec(s))) {
    if (m.index > last) out.push(s.slice(last, m.index));
    const label = m[1] || m[3];
    const href = m[2] || m[3];
    out.push(
      <a key={out.length} href={href} target="_blank" rel="noreferrer" className="text-nav-active underline underline-offset-2 break-all">
        {label}
      </a>,
    );
    last = re.lastIndex;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

// btnPrimary/btnGhost/fieldCls/sectionLabel come from the shared form tokens
// in requestMeta — one visual language across every task window.

// Kinds offered by the "New task" dialog: the buildable new_* kinds plus the
// change_* kinds (grouped in the select). Change-targets (app/agent) load
// dynamically for whichever project is selected; workflow/pipeline changes
// name their target in the details, same as their home pages; change_project
// targets the selected project itself. Investigation is chat-promoted only.
// NOTE: the type is ALWAYS the user's explicit selection. An LLM-classified
// kind ("Let the agent decide") shipped TWICE and was removed TWICE: first
// dev runs #91/#93 (the worker overrode the user's explicit type wish), then
// prod #95 (a multi-deliverable brief filed from inside a project classified
// as new_project and was built into a SEPARATE project). Choosing what gets
// built — app, workflow, project… — is the user's decision, not the agent's.
const TASK_KINDS = [
  {
    kind: "new_app", ...REQUEST_KIND_META.new_app, group: "Create new",
    description: "An interactive app over this project's data — dashboard, CRUD, a tool.",
    titlePlaceholder: "What should the app be called / do?",
    detailsPlaceholder: "Describe what you need in plain language — data to show, actions, who it's for…",
    fileHint: "Attach a mockup, screenshot, or data file (optional)",
  },
  {
    kind: "new_agent", ...REQUEST_KIND_META.new_agent, group: "Create new",
    description: "A conversational agent that works over the project's data and apps.",
    titlePlaceholder: "What should the agent be called / do?",
    detailsPlaceholder: "Describe the agent in plain language — what it should do, the data/apps it should work over, who it's for…",
    fileHint: "Attach a spec document (optional)",
  },
  {
    kind: "new_workflow", ...REQUEST_KIND_META.new_workflow, group: "Create new",
    description: "A business process — steps, human approvals, agent actors, triggers.",
    titlePlaceholder: "What should the process be called / do?",
    detailsPlaceholder: "Describe the process in plain language — the steps, who approves what, which steps AI agents should handle, what starts a run…",
    fileHint: "Attach a spec document (optional)",
  },
  {
    kind: "new_pipeline", ...REQUEST_KIND_META.new_pipeline, group: "Create new",
    description: "A governed data pipeline: sources → elements → rules → target, with a verified run.",
    titlePlaceholder: "What should the pipeline be called / do?",
    detailsPlaceholder: "Describe the data in plain language — what it looks like, where it comes from, where it should land, any rules…",
    fileHint: "Attach a data sample or description (.csv, .xlsx, .zip, .docx…)",
  },
  {
    kind: "new_source", ...REQUEST_KIND_META.new_source, group: "Create new",
    label: "New data source",
    description: "A connector to a system cortex can't reach yet — an agent researches it and builds the connection form.",
    titlePlaceholder: "Which system should cortex connect to?",
    detailsPlaceholder: "Describe it in plain language — the service or database, its URL or docs if you have them, what records you need, and how you authenticate…",
    fileHint: "Attach API docs, a schema, or a sample export (optional)",
  },
  {
    kind: "new_project", ...REQUEST_KIND_META.new_project, group: "Create new",
    description: "A whole new project: governed database, workflows, agents, and an app.",
    titlePlaceholder: "What should the project be called / do?",
    detailsPlaceholder: "Describe the whole project in plain language — the domain, the data, the processes, who will use it… (paste a document if you have one)",
    fileHint: "Attach a requirements document (optional)",
    noProject: true,
  },
  {
    kind: "change", ...REQUEST_KIND_META.change, group: "Change existing",
    label: "App — change",
    description: "Build a change into an existing app of the selected project.",
    needsTarget: true, targetLabel: "Select an app…", targetField: "app_id",
    titlePlaceholder: "Short summary",
    detailsPlaceholder: "Describe what should change — data to show, actions, layout…",
    fileHint: "Attach a mockup or screenshot (optional)",
  },
  {
    kind: "bug_fix", ...REQUEST_KIND_META.bug_fix, group: "Change existing",
    label: "App — bug fix",
    description: "Something in an existing app of the selected project is broken.",
    needsTarget: true, targetLabel: "Select an app…", targetField: "app_id",
    titlePlaceholder: "Short summary",
    detailsPlaceholder: "What's broken? What did you expect to happen?",
    fileHint: "Attach a screenshot of the bug (optional)",
  },
  {
    kind: "change_agent", ...REQUEST_KIND_META.change_agent, group: "Change existing",
    label: "Agent — change",
    description: "A change to an existing agent of the selected project.",
    needsTarget: true, targetLabel: "Select an agent…", targetField: "agent_id",
    titlePlaceholder: "Short summary",
    detailsPlaceholder: "Describe what should change about this agent…",
    fileHint: "Attach a spec document (optional)",
  },
  {
    kind: "change_workflow", ...REQUEST_KIND_META.change_workflow, group: "Change existing",
    label: "Workflow — change",
    description: "A change to an existing business process — name the process in the description.",
    titlePlaceholder: "Short summary",
    detailsPlaceholder: "Which process, and what should change — steps, assignments, outcomes, triggers…",
    fileHint: "Attach a spec document (optional)",
  },
  {
    kind: "change_pipeline", ...REQUEST_KIND_META.change_pipeline, group: "Change existing",
    label: "Pipeline — change",
    description: "A change to an existing data pipeline — name the pipeline in the description.",
    titlePlaceholder: "Short summary",
    detailsPlaceholder: "Which pipeline, and what should change — new sources, mappings, rules, targets…",
    fileHint: "Attach a data sample or description (optional)",
  },
  {
    // No connector picker here, unlike the Data Sources page's own dialog: the
    // Board files cross-project, so the connector list would have to reload per
    // selected project. Naming the connector in the description is the same
    // contract the source executor already expects, and matches how workflow
    // and pipeline changes name their target here.
    kind: "change_source", ...REQUEST_KIND_META.change_source, group: "Change existing",
    label: "Data source — change",
    description: "A change to an existing connector — name the connector in the description.",
    titlePlaceholder: "Short summary",
    detailsPlaceholder: "Which connector, and what's wrong or missing — a field the form should collect, a different endpoint, authentication that stopped working…",
    fileHint: "Attach an error message, docs, or a sample response (optional)",
  },
  {
    kind: "change_project", ...REQUEST_KIND_META.change_project, group: "Change existing",
    label: "Project — change",
    description: "Changes across the WHOLE selected project — recipe, workflows, agents, and app together.",
    projectHint: "The project to change.",
    titlePlaceholder: "Short summary",
    detailsPlaceholder: "Describe the change across the project — data, processes, agents, app… (paste a document if you have one)",
    fileHint: "Attach a requirements document (optional)",
  },
];

function StatusChip({ status, queuePosition }) {
  const meta = STATUS_META[status] || STATUS_META.queued;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.cls}`}>
      <Icon size={12} className={meta.spin ? "animate-spin" : ""} />
      {status === "queued" && queuePosition ? `#${queuePosition} in queue` : meta.label}
    </span>
  );
}

function KindLine({ r }) {
  const meta = REQUEST_KIND_META[r.kind];
  const Icon = meta?.icon;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-[var(--page-muted,#6b7280)] min-w-0">
      {Icon && <Icon size={12} className="shrink-0" />}
      <span className="truncate">{meta?.label || r.kind?.replace(/_/g, " ")}</span>
    </span>
  );
}

function openAppUrl(r) {
  return r.result?.url || (r.app?.view_name
    ? `/p/${encodeURIComponent(r.project_slug)}/b/main/apps/${encodeURIComponent(r.app.view_name)}`
    : null);
}

function Card({ r, parent, childCount, draggable, busy, onDragStart, onDragEnd, onOpen, onOpenTask, onOpenComments, onCancel, onDismiss, onSetType, dragging, foreign }) {
  const stale = isStale(r);
  const inFlight = OPEN_STATUSES.has(r.status);
  const terminal = TERMINAL_STATUSES.has(r.status);
  const metrics = r.result?.metrics;
  // Tokens that ENTERED the session's context (input + cache creation) — the
  // same figure the live ticker counts ("… — Bash #78 · 292k tok"), so the
  // finished card agrees with its own Activity log. The earlier "total
  // processed" sum (cache reads included) was ~96% cache re-reads of that
  // same context on every turn (9.0M vs 295k on request #366) — a real
  // volume, but not what a person means by "how much did it read"; the
  // operator view on the Agent-fleet page keeps the full breakdown.
  const metricsTok = metrics
    ? (metrics.input_tokens || 0) + (metrics.cache_creation_tokens || 0)
    : 0;
  // In-flight (claimed/running) cards get a Stop instead of Cancel: same
  // server verb, but it halts a LIVE agent (the page-level handler confirms
  // first; the fleet dispatcher kills the worker process of a cancelled row).
  const inFlightStop = r.status === "claimed" || r.status === "running";
  const cancellable = r.status === "queued" || r.status === "todo" || r.status === "waiting" || inFlightStop;
  return (
    <div
      data-testid={`board-card-${r.id}`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      className={
        "group relative rounded-lg border border-card-border bg-card-bg p-3 cursor-pointer hover:border-nav-active/50 transition-colors" +
        (draggable ? " active:cursor-grabbing" : "") +
        (dragging ? " opacity-40" : "")
      }
    >
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <span className="text-xs font-mono text-[var(--page-muted,#6b7280)]">#{r.id}</span>
        {/* Teammate's task (Team scope): name the requester — whose ask you
            are looking at is the first thing team triage needs to know. */}
        {foreign && (
          <span
            className="inline-flex items-center gap-1 text-[10px] px-1.5 rounded-full border border-card-border text-[var(--page-muted,#6b7280)]"
            title={`Filed by ${foreign.email}`}
          >
            <CircleUser size={10} /> {foreign.name || foreign.email.split("@")[0]}
          </span>
        )}
        {/* The column already names the status — the chip earns its place
            only where it ADDS something: the queue position, the
            claimed/running distinction inside the folded Running column,
            and the failed/cancelled distinction inside folded Stopped. */}
        {(r.status === "queued" || r.status === "claimed" || r.status === "running" || r.status === "failed" || r.status === "cancelled") && (
          <StatusChip status={r.status} queuePosition={r.queue_position} />
        )}
        {Array.isArray(r.attachments) && r.attachments.length > 0 && (
          <span
            className="inline-flex items-center gap-1 text-[10px] px-1.5 rounded-full border border-card-border text-[var(--page-muted,#6b7280)]"
            title={`${r.attachments.length} attachment${r.attachments.length === 1 ? "" : "s"}`}
          >
            <Paperclip size={10} /> {r.attachments.length}
          </span>
        )}
        {childCount > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 rounded-full border border-card-border text-[var(--page-muted,#6b7280)]" title={`${childCount} task${childCount === 1 ? "" : "s"} attached to this one`}>
            <Link2 size={10} /> {childCount}
          </span>
        )}
        {/* Graph-edge count (both directions). The chip opens the window, where
            the edges are listed and authorable — same affordance as 💬. */}
        {r.link_count > 0 && (
          <button
            type="button"
            data-testid={`board-link-chip-${r.id}`}
            className="inline-flex items-center gap-1 text-[10px] px-1.5 rounded-full border border-card-border text-[var(--page-muted,#6b7280)] bg-transparent cursor-pointer hover:text-nav-active hover:border-nav-active/40"
            title={`${r.link_count} related task${r.link_count === 1 ? "" : "s"}`}
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
          >
            <Link2 size={10} /> {r.link_count}
          </button>
        )}
        {r.comment_count > 0 && (
          <button
            type="button"
            data-testid={`board-comment-chip-${r.id}`}
            className="inline-flex items-center gap-1 text-[10px] px-1.5 rounded-full border border-card-border text-[var(--page-muted,#6b7280)] bg-transparent cursor-pointer hover:text-nav-active hover:border-nav-active/40"
            title={`${r.comment_count} comment${r.comment_count === 1 ? "" : "s"} — open the thread`}
            onClick={(e) => { e.stopPropagation(); onOpenComments(r.id); }}
          >
            <MessageSquare size={10} /> {r.comment_count}
          </button>
        )}
        {r.dismissed_at ? (
          <span className="text-[10px] px-1.5 rounded-full border border-card-border text-[var(--page-muted,#6b7280)]" title={`Cleared ${new Date(r.dismissed_at).toLocaleString()}`}>cleared</span>
        ) : null}
      </div>
      {r.parent_request_id != null && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenTask(r.parent_request_id); }}
          className="inline-flex items-center gap-1 text-xs text-[var(--page-muted,#6b7280)] hover:text-nav-active bg-transparent border-0 cursor-pointer p-0 mb-1 max-w-full"
          title="Open the task this one is attached to"
        >
          <CornerDownRight size={11} className="shrink-0" />
          <span className="truncate">#{r.parent_request_id}{parent ? ` ${parent.title}` : ""}</span>
        </button>
      )}
      <div className="text-sm font-medium text-[var(--page-title,#111)] break-words">{r.title}</div>
      <div className="flex items-center gap-2 mt-1 min-w-0">
        {/* An untyped (triage) capture has NO type — showing its "Task" label
            here read as a chosen type, and users only learned otherwise when
            the queue refused the drag. The state renders as the ACTION it
            needs instead, always visible (the old hover-only button was
            invisible until pointed at). */}
        {r.kind === "triage" && r.status === "todo" ? (
          <button
            type="button"
            disabled={busy}
            data-testid={`board-set-type-${r.id}`}
            title="Pick a type so this task can be taken into work"
            onClick={(e) => { e.stopPropagation(); onSetType(r.id); }}
            className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-md border border-[var(--severity-warning-text,#92400e)]/40 text-[var(--severity-warning-text,#92400e)] bg-[var(--severity-warning-bg,#fef3c7)] hover:border-[var(--severity-warning-text,#92400e)] cursor-pointer disabled:opacity-50"
          >
            <CircleDashed size={11} className="shrink-0" /> Set type
          </button>
        ) : (
          <KindLine r={r} />
        )}
        {/* The TARGET of a change/bug_fix (app) or change_agent (agent) —
            the payload already carries the joined labels. min-w-0 matters:
            flex children refuse to shrink below their text without it, and
            a long target name forces the column into horizontal scroll. */}
        {(r.app?.title || r.app?.view_name || r.agent) && (
          <span className="text-xs text-[var(--page-muted,#6b7280)] truncate min-w-0" title="Target of this change">
            → {r.app?.title || r.app?.view_name || r.agent}
          </span>
        )}
        <span className="text-xs text-[var(--page-muted,#6b7280)] truncate min-w-0">· {r.project_slug}</span>
      </div>
      {inFlight ? (
        <div className="text-xs text-[var(--page-muted,#6b7280)] mt-1.5">
          <div className="truncate">{r.status === "queued" ? queueHint(r.queue_position, r.org_in_flight) : (r.last_log || "Working…")}</div>
          <div className="mt-0.5">
            {r.status === "queued" ? `In queue ${elapsed(r.created_at)}` : `Running ${elapsed(r.claimed_at || r.created_at)}`}
          </div>
        </div>
      ) : (
        <div className="text-xs text-[var(--page-muted,#6b7280)] truncate mt-1.5">
          {r.status === "todo" ? `Filed ${timeAgo(r.created_at)}` : `${r.last_log || ""} · ${timeAgo(r.finished_at || r.updated_at)}`}
        </div>
      )}
      {/* Agent effort (fleet-measured): LLM time + tokens spent on this task. */}
      {terminal && metrics && (metricsTok > 0 || metrics.llm_ms > 0 || metrics.wall_ms > 0) && (
        <div className="inline-flex items-center gap-1 text-xs text-[var(--page-muted,#6b7280)] mt-1" title="Agent effort: LLM time · tokens read into the session's context">
          <Zap size={11} className="shrink-0" />
          {fmtMs(metrics.llm_ms || metrics.wall_ms)}{metrics.llm_ms || metrics.wall_ms ? " LLM" : ""}
          {metricsTok > 0 ? ` · ${fmtTokens(metricsTok)} tok` : ""}
        </div>
      )}
      {stale && (
        <div className="text-xs text-[var(--severity-warning-text,#92400e)] mt-1">
          Stuck for over 30 minutes — you can cancel it.
        </div>
      )}
      {/* Hover actions: Cancel (parked) / Stop (in-flight), ✕ dismiss (terminal). */}
      {(cancellable || (terminal && !r.dismissed_at)) && (
        <div className="absolute top-2 right-2 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {cancellable && (
            <button
              type="button"
              disabled={busy}
              data-testid={`board-${inFlightStop ? "stop" : "cancel"}-${r.id}`}
              title={inFlightStop ? "Stop the agent and cancel this task" : "Cancel this task"}
              onClick={() => onCancel(r.id)}
              className="text-xs font-medium px-2 py-0.5 rounded-md border border-card-border text-[var(--severity-warning-text,#92400e)] hover:bg-[var(--table-stripe,#f3f4f6)] disabled:opacity-50 cursor-pointer bg-card-bg opacity-0 group-hover:opacity-100 transition-opacity"
            >
              {inFlightStop ? "Stop" : "Cancel"}
            </button>
          )}
          {terminal && !r.dismissed_at && (
            <button
              type="button"
              disabled={busy}
              aria-label="Remove from board"
              title="Remove from board"
              onClick={() => onDismiss(r.id)}
              className="inline-flex items-center justify-center size-6 rounded-md text-[var(--page-muted,#6b7280)] hover:bg-[var(--table-stripe,#f3f4f6)] hover:text-[var(--page-title,#111)] disabled:opacity-50 cursor-pointer bg-card-bg border border-card-border opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X size={13} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// The task window — a centered Jira-style modal. Single column: the type row
// sits ABOVE a full-width brief (the brief is what needs the room). For the
// statuses the user may drag (todo/failed/cancelled) the fields are LIVE from
// the moment it opens, committed by ONE Save; other statuses render read-only
// with the task-level actions. Closing with unsaved edits asks first.
//
// Untyped (triage) captures open on their own "Task" (untyped) entry; the
// user picks a concrete type from the same select as every other card — the
// type is always a manual choice, never classified. Staying untyped is fine
// for saving text edits; the backend's queue gate still demands a real type
// before the card may enter the queue.
// Graph edges for one task (app_request_link): both directions, authorable.
// Loaded per open task rather than with the board list — the list only carries
// link_count, and a card's edges are only needed once its window is open.
// Wording is direction-aware ("blocks" outgoing reads as "blocks", incoming as
// "blocked by") so an edge means the same thing from either end.
const LINK_LABEL_OUT = { relates: "relates to", blocks: "blocks", supersedes: "supersedes", duplicates: "duplicates" };
const LINK_LABEL_IN = { relates: "relates to", blocks: "blocked by", supersedes: "superseded by", duplicates: "duplicated by" };

function TaskLinks({ id, onOpenTask }) {
  const [links, setLinks] = useState(null); // null = loading
  const [adding, setAdding] = useState(false);
  const [peer, setPeer] = useState("");
  const [kind, setKind] = useState("relates");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setLinks(await fetchRequestLinks(id)); }
    catch { setLinks({ outgoing: [], incoming: [] }); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const to = Number(String(peer).replace(/^#/, "").trim());
    if (!Number.isInteger(to) || to <= 0) { toast.error("Enter a task id, e.g. 42"); return; }
    setBusy(true);
    try {
      const res = await linkRequest(id, to, kind);
      if (res?.link?.created === false) toast.info(`Already linked to #${to}`);
      setPeer(""); setAdding(false);
      await load();
    } catch (e) {
      toast.error(e?.message || `Could not link to #${to}`);
    } finally { setBusy(false); }
  };

  const remove = async (linkId) => {
    setBusy(true);
    try { await unlinkRequest(id, linkId); await load(); }
    catch (e) { toast.error(e?.message || "Could not remove the link"); }
    finally { setBusy(false); }
  };

  const rows = [
    ...(links?.outgoing || []).map((l) => ({ ...l, dir: "out" })),
    ...(links?.incoming || []).map((l) => ({ ...l, dir: "in" })),
  ];

  return (
    <div data-testid="task-links">
      <div className={sectionLabel + " mb-1 flex items-center gap-2"}>
        <span>Related tasks</span>
        <button
          type="button"
          data-testid="task-link-add"
          onClick={() => setAdding((v) => !v)}
          className="text-nav-active hover:underline bg-transparent border-0 cursor-pointer p-0 text-xs normal-case tracking-normal"
        >
          {adding ? "cancel" : "+ link"}
        </button>
      </div>
      {adding && (
        <div className="flex items-center gap-2 mb-2">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="h-7 rounded-md border border-card-border bg-card-bg px-1.5 text-xs text-page-title"
          >
            {LINK_KINDS.map((k) => <option key={k} value={k}>{LINK_LABEL_OUT[k]}</option>)}
          </select>
          <input
            data-testid="task-link-peer"
            value={peer}
            onChange={(e) => setPeer(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            placeholder="task id, e.g. 42"
            className="h-7 w-32 rounded-md border border-card-border bg-card-bg px-2 text-xs text-page-title"
          />
          <button type="button" data-testid="task-link-save" onClick={add} disabled={busy} className={btnGhost + " h-7"}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : "Link"}
          </button>
        </div>
      )}
      {links === null ? (
        <div className="text-xs text-[var(--page-muted,#6b7280)]">loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-[var(--page-muted,#6b7280)]">No related tasks.</div>
      ) : (
        <div className="flex flex-col gap-1">
          {rows.map((l) => (
            <div key={`${l.dir}-${l.link_id}`} className="flex items-center gap-2 text-sm min-w-0">
              <Link2 size={12} className="text-[var(--page-muted,#6b7280)] shrink-0" />
              <span className="text-xs text-[var(--page-muted,#6b7280)] shrink-0">
                {(l.dir === "out" ? LINK_LABEL_OUT : LINK_LABEL_IN)[l.kind] || l.kind}
              </span>
              <button
                type="button"
                onClick={() => onOpenTask(l.task.id)}
                className="text-nav-active hover:underline bg-transparent border-0 cursor-pointer p-0 truncate"
              >
                #{l.task.id} {l.task.title}
              </button>
              <StatusChip status={l.task.status} />
              <button
                type="button"
                title="Remove this link"
                onClick={() => remove(l.link_id)}
                disabled={busy}
                className="ml-auto text-[var(--page-muted,#6b7280)] hover:text-red-500 bg-transparent border-0 cursor-pointer p-0 shrink-0"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskModal({ r, parent, childTasks, onOpenTask, onClose, onDismiss, onCancel, onEdited, taskKinds, loadTargets, busy, focusComments }) {
  const { user: me } = useAuth();
  const url = r ? openAppUrl(r) : null;
  const terminal = r && TERMINAL_STATUSES.has(r.status);
  const report = typeof r?.result?.report === "string" && r.result.report.trim() ? r.result.report : null;
  // Editable = exactly the statuses the user may drag (BOARD_MOVES sources);
  // the server enforces the same set (409 on a race with a worker claim).
  const editable = r && (r.status === "todo" || r.status === "waiting" || r.status === "failed" || r.status === "cancelled");
  const [saving, setSaving] = useState(false);
  const { confirm } = useConfirm();
  const [eTitle, setETitle] = useState("");
  const [eDetails, setEDetails] = useState("");
  const [eKind, setEKind] = useState("");
  const [eTargetId, setETargetId] = useState(null);
  const [targets, setTargets] = useState(null);
  // A file picked in edit mode — uploaded on Save, APPENDED to the row's
  // attachments (PATCH add_attachments).
  const [newFiles, setNewFiles] = useState([]);
  const [newFileErr, setNewFileErr] = useState(null);
  // The card's comment thread (feedback loop) — fetched separately (the board
  // list payload carries only comment_count) and kept live while the window
  // is open, so an executor's clarifying question shows up mid-conversation.
  const [comments, setComments] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  // Jira-style composer: a collapsed "Add a comment…" one-liner that expands
  // into the real editor on click — the CTA text IS the affordance (the old
  // side-button was invisible enough that real users failed to post twice).
  const [composerOpen, setComposerOpen] = useState(false);
  const [showAllLog, setShowAllLog] = useState(false);
  const [showFullReport, setShowFullReport] = useState(false);
  // The board list caps each card's log at its last 20 entries; the dialog
  // fetches the complete trail (fullLog) and falls back to the capped row
  // until it arrives.
  const [fullLog, setFullLog] = useState(null);
  // Chronological Activity: backfilled history (a promoted investigation's
  // pre-promotion lines) is APPENDED to the array with original timestamps,
  // so array order ≠ time order — sort by `at` for display.
  const orderedLog = useMemo(() => {
    const src = fullLog || r?.log || [];
    return [...src].sort((a, b) => String(a?.at || "").localeCompare(String(b?.at || "")));
  }, [fullLog, r?.log]);
  const commentsRef = useRef(null);
  useEffect(() => {
    if (!r?.id) { setComments(null); setFullLog(null); return; }
    setComments(null);
    setFullLog(null);
    setCommentText("");
    setComposerOpen(false);
    setShowAllLog(false);
    setShowFullReport(false);
    let gone = false;
    // One fetch feeds both dialog needs: the comment thread AND the complete
    // Activity trail (the board list caps each card's log at the last 20
    // entries — a long run's backfilled history only exists here).
    const load = () => fetchRequest(r.id)
      .then((out) => {
        if (gone) return;
        setComments(out?.comments || []);
        if (Array.isArray(out?.log)) setFullLog(out.log);
      })
      .catch(() => {});
    load();
    const t = setInterval(load, POLL_MS);
    return () => { gone = true; clearInterval(t); };
  }, [r?.id]);
  // Opened via the card's 💬 chip: land on the thread, composer ready.
  useEffect(() => {
    if (!r?.id || !focusComments) return;
    setComposerOpen(true);
    const t = setTimeout(() => commentsRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }), 80);
    return () => clearTimeout(t);
  }, [r?.id, focusComments]); // eslint-disable-line react-hooks/exhaustive-deps
  const postComment = async () => {
    const body = commentText.trim();
    if (!body) return;
    setPosting(true);
    try {
      const out = await addRequestComment(r.id, body);
      if (out?.comment) setComments((cur) => [...(cur || []), out.comment]);
      setCommentText("");
      setComposerOpen(false); // collapse back — the new comment lands right below
      // The one non-obvious mechanic of a waiting card, surfaced exactly when
      // it matters: an answered card does NOT resume by itself.
      if (r.status === "waiting") {
        toast.success("Comment posted — drag the card to Queued when you're ready to resume.");
      }
      onEdited(); // board list refresh — the card's comment chip follows
    } catch (e) {
      toast.error(e?.message || "could not post the comment");
    } finally {
      setPosting(false);
    }
  };
  // A kind outside taskKinds (the row's own — e.g. 'investigation') still
  // needs its option, or the select would silently coerce it to the first
  // entry. 'triage' is deliberately NOT given an option: untyped is not a
  // type, so the select renders the same disabled "Select a type…"
  // placeholder as the New-task dialog until the user picks a real kind.
  const fallbackKindEntry = (kind) => {
    const meta = REQUEST_KIND_META[kind] || {};
    return { kind, label: meta.label || kind, needsTarget: kind === "change" || kind === "bug_fix" || kind === "change_agent", targetField: kind === "change_agent" ? "agent_id" : "app_id", targetLabel: "Select…" };
  };
  const kindsForEdit = useMemo(() => {
    const base = [...(taskKinds || [])];
    for (const k of [r?.kind, eKind]) {
      if (k && k !== "triage" && !base.some((x) => x.kind === k)) base.unshift(fallbackKindEntry(k));
    }
    return base;
  }, [taskKinds, r, eKind]);
  const eMeta = kindsForEdit.find((k) => k.kind === eKind);
  // Seed the fields from the row on open / when another task opens.
  useEffect(() => {
    if (!r) return;
    setETitle(r.title || "");
    setEDetails(r.details || "");
    setEKind(r.kind);
    // The API carries the target as OBJECTS (app / target_agent), never as
    // bare app_id/agent_id — reading the flat fields seeded null, the single-
    // option auto-pick then "chose" a target, and the window opened dirty.
    setETargetId(r.app?.id ?? r.target_agent?.id ?? null);
    // Attachments are a LIST (newFiles/setNewFiles) — the old singular
    // setNewFile(null) was a ReferenceError that crashed the whole task
    // window on open ("Something went wrong"), since this reset runs on every
    // r.id change.
    setNewFiles([]);
    setNewFileErr(null);
  }, [r?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Load the target options whenever the edited kind needs one; keep the
  // row's existing target when it fits, else auto-pick a single option.
  useEffect(() => {
    if (!editable || !eMeta?.needsTarget) { setTargets(null); return; }
    let gone = false;
    loadTargets(eKind, r.project_slug)
      .then((opts) => {
        if (gone) return;
        setTargets(opts || []);
        setETargetId((cur) => {
          if (cur != null && (opts || []).some((o) => String(o.id) === String(cur))) return cur;
          return (opts || []).length === 1 ? opts[0].id : null;
        });
      })
      .catch(() => { if (!gone) setTargets([]); });
    return () => { gone = true; };
  }, [editable, eKind, r?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = !!(editable && r && (
    eTitle !== (r.title || "") ||
    eDetails !== (r.details || "") ||
    eKind !== r.kind ||
    String(eTargetId ?? "") !== String((r.app?.id ?? r.target_agent?.id) ?? "") ||
    newFiles.length > 0
  ));
  // Commit EVERYTHING pending in the window — brief edits AND a typed-but-
  // unposted comment ("I wrote a comment, Save is right there, it's disabled"
  // was a real user trap). Brief validation only applies when the brief
  // actually changed (a comment-only save must not demand a target). Shared
  // by Save (the only footer commit — status moves live on the board).
  const commitPending = async () => {
    const pendingComment = commentText.trim();
    if (dirty) {
      if (!eTitle.trim()) { toast.error("title cannot be empty"); return false; }
      if (eMeta?.needsTarget && eTargetId == null) { toast.error("pick a target"); return false; }
    }
    if (pendingComment) {
      await addRequestComment(r.id, pendingComment);
      setCommentText("");
    }
    if (dirty) {
      // Upload the newly picked files FIRST — a failed upload aborts the save
      // instead of updating the brief without its documents.
      const prep = await prepareAttachments(newFiles);
      await editRequest(r.id, {
        title: eTitle.trim(),
        details: eDetails,
        kind: eKind,
        app_id: eMeta?.targetField === "app_id" ? Number(eTargetId) : null,
        agent_id: eMeta?.targetField === "agent_id" ? Number(eTargetId) : null,
        ...(prep.attachments ? { add_attachments: prep.attachments } : {}),
      });
    }
    return true;
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      if (!(await commitPending())) return;
      toast.success(
        (dirty ? "Task updated." : "Comment posted.") +
        (r.status === "waiting" ? " Drag the card to Queued when you're ready to resume." : ""),
      );
      onEdited();
      onClose();
    } catch (e) {
      toast.error(e?.message || "edit failed");
    } finally {
      setSaving(false);
    }
  };
  const requestClose = async () => {
    // Styled in-product confirm (useConfirm/AlertDialog) — the Dialog is
    // controlled, so it simply stays open until we call onClose. A typed but
    // unposted comment counts as unsaved work too.
    if ((dirty || commentText.trim()) && !(await confirm({
      title: "Discard unsaved changes?",
      description: dirty ? "Your edits to this task will be lost." : "Your typed comment has not been posted.",
      confirmText: "Discard",
      cancelText: "Keep editing",
    }))) return;
    onClose();
  };
  const openAttachment = async (idx) => {
    try {
      const res = await requestAttachmentUrl(r.id, idx);
      if (res?.url) window.open(res.url, "_blank", "noopener");
    } catch (e) { toast.error(e?.message || "could not fetch the attachment"); }
  };
  return (
    <Dialog open={!!r} onOpenChange={(open) => { if (!open) requestClose(); }}>
      <DialogContent className="bg-card-bg border-card-border p-0 gap-0 sm:max-w-[920px] w-[95vw]" data-testid="board-task-modal">
        {r && (
          <div className="flex flex-col max-h-[85vh] min-h-0">
            {/* Header: identity row + title (live input on editable statuses). */}
            <div className="px-6 pt-5 pb-4 border-b border-card-border">
              <div className="flex items-center gap-2 flex-wrap pr-8">
                <button
                  type="button"
                  title="Copy task id"
                  onClick={() => { try { navigator.clipboard.writeText(`#${r.id}`); toast.success(`Copied #${r.id}`); } catch { /* clipboard unavailable */ } }}
                  className="inline-flex items-center gap-1 text-xs font-mono text-[var(--page-muted,#6b7280)] hover:text-nav-active bg-transparent border-0 cursor-pointer p-0"
                >
                  #{r.id} <Copy size={11} />
                </button>
                <StatusChip status={r.status} queuePosition={r.queue_position} />
                <span className="text-xs text-[var(--page-muted,#6b7280)]">· {r.project_slug}</span>
              </div>
              {editable ? (
                <input
                  value={eTitle}
                  onChange={(e) => setETitle(e.target.value)}
                  data-testid="board-edit-title"
                  className="w-full mt-2 text-lg font-semibold text-[var(--page-title,#111)] bg-page-bg border border-card-border rounded-md px-2.5 py-1.5 outline-none focus:border-nav-active"
                />
              ) : (
                <div className="text-lg font-semibold text-[var(--page-title,#111)] mt-2 break-words">{r.title}</div>
              )}
              <div className="text-xs text-[var(--page-muted,#6b7280)] mt-1">
                Filed {timeAgo(r.created_at)}
                {r.agent ? ` · agent: ${r.agent}` : ""}
                {r.finished_at ? ` · finished ${timeAgo(r.finished_at)}` : (r.claimed_at ? ` · running ${elapsed(r.claimed_at)}` : "")}
              </div>
            </div>
            {/* Body — single column: the type row ABOVE a full-width brief. */}
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 flex flex-col gap-4">
              <div>
                <div className={sectionLabel + " mb-1"}>Type</div>
                {editable ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <select
                        value={eKind === "triage" ? "" : eKind}
                        onChange={(e) => setEKind(e.target.value || "triage")}
                        data-testid="board-edit-kind"
                        className={fieldCls}
                      >
                        {/* Untyped row: placeholder holds the slot until a
                            real type is picked (it can't be re-selected —
                            same contract as the New-task dialog). Saving
                            while it still shows is fine: eKind stays
                            'triage', text edits commit, the queue gate
                            still blocks the drag. */}
                        {(r.kind === "triage" || eKind === "triage") && (
                          <option value="" disabled>Select a type…</option>
                        )}
                        {kindsForEdit.map((k) => (
                          <option key={k.kind} value={k.kind}>{k.label}</option>
                        ))}
                      </select>
                      {eMeta?.needsTarget && (
                        <select
                          value={eTargetId ?? ""}
                          onChange={(e) => setETargetId(e.target.value || null)}
                          data-testid="board-edit-target"
                          className={fieldCls + " min-w-0 flex-1"}
                        >
                          <option value="">{targets === null ? "Loading…" : (eMeta.targetLabel || "Select…")}</option>
                          {(targets || []).map((o) => (
                            <option key={o.id} value={o.id}>{o.label}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 min-w-0">
                    <KindLine r={r} />
                    {(r.app?.title || r.app?.view_name || r.agent) && (
                      <span className="text-xs text-[var(--page-muted,#6b7280)] truncate min-w-0">→ {r.app?.title || r.app?.view_name || r.agent}</span>
                    )}
                  </div>
                )}
              </div>
              <div>
                <div className={sectionLabel + " mb-1"}>Brief</div>
                {editable ? (
                  <textarea
                    value={eDetails}
                    onChange={(e) => setEDetails(e.target.value)}
                    rows={14}
                    data-testid="board-edit-details"
                    className={fieldCls + " w-full resize-y min-h-[300px]"}
                  />
                ) : (
                  r.details
                    ? <div className="text-sm text-page-text whitespace-pre-wrap break-words">{r.details}</div>
                    : <div className="text-xs text-[var(--page-muted,#6b7280)]">No brief.</div>
                )}
              </div>
              {((Array.isArray(r.attachments) && r.attachments.length > 0) || editable) && (
                <div>
                  <div className={sectionLabel + " mb-1"}>Attachments</div>
                  <div className="flex flex-col gap-1">
                    {(r.attachments || []).map((a, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => openAttachment(i)}
                        className="inline-flex items-center gap-1.5 text-sm text-nav-active hover:underline bg-transparent border-0 cursor-pointer p-0 text-left min-w-0"
                      >
                        <Paperclip size={13} className="shrink-0" /> <span className="truncate">{a.name}</span>
                      </button>
                    ))}
                    {editable && (
                      <div className="mt-1">
                        <AttachmentPicker
                          files={newFiles}
                          onChange={setNewFiles}
                          onError={setNewFileErr}
                          hint="Attach documents, data samples, or screenshots (optional)"
                          testId="board-edit-file"
                        />
                        {newFileErr && <div className="text-sm text-red-500 mt-1">{newFileErr}</div>}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* The feedback thread — directly under the brief/attachments:
                  the live conversation is the most actionable part of a card.
                  Executors ask clarifying questions here, people answer; the
                  thread rides into the worker's claim when the card is
                  retried. Jira-style: the composer sits ON TOP of the thread,
                  collapsed into an "Add a comment…" one-liner until clicked. */}
              <div ref={commentsRef}>
                <div className={sectionLabel + " mb-1"}>Comments{comments?.length ? ` (${comments.length})` : ""}</div>
                {!composerOpen ? (
                  <button
                    type="button"
                    data-testid="board-comment-open"
                    onClick={() => setComposerOpen(true)}
                    className="w-full flex items-center gap-2 rounded-md border border-card-border bg-card-bg px-2.5 py-2 text-xs text-[var(--page-muted,#6b7280)] cursor-text text-left hover:border-nav-active/50"
                  >
                    <span className="w-5 h-5 rounded-full bg-nav-active/15 text-nav-active inline-flex items-center justify-center text-[10px] font-semibold shrink-0">
                      {String(me?.first_name || me?.email || "?").charAt(0).toUpperCase()}
                    </span>
                    Add a comment…
                  </button>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    <textarea
                      autoFocus
                      data-testid="board-comment-input"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") postComment(); }}
                      placeholder="Add a comment… (the executor reads this thread)"
                      rows={3}
                      maxLength={4000}
                      className="w-full rounded-md border border-card-border bg-card-bg text-page-text text-xs px-2.5 py-1.5 resize-y min-h-[3.5rem]"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        data-testid="board-comment-send"
                        disabled={posting || !commentText.trim()}
                        onClick={postComment}
                        className="inline-flex items-center gap-1.5 rounded-md bg-nav-active text-white text-xs px-3 py-1.5 border-0 cursor-pointer hover:bg-nav-active/90 disabled:opacity-50"
                      >
                        {posting ? <Loader2 size={13} className="animate-spin" /> : <MessageSquare size={13} />} Comment
                      </button>
                      <button
                        type="button"
                        className={btnGhost}
                        onClick={() => { setCommentText(""); setComposerOpen(false); }}
                      >
                        Cancel
                      </button>
                      <span className="text-[10px] text-[var(--page-muted,#6b7280)]">Ctrl+Enter — send</span>
                    </div>
                  </div>
                )}
                {comments === null ? (
                  <div className="text-xs text-[var(--page-muted,#6b7280)] mt-2">Loading…</div>
                ) : comments.length === 0 ? (
                  <div className="text-xs text-[var(--page-muted,#6b7280)] mt-2">No comments yet — feedback for the executor and its questions land here.</div>
                ) : (
                  <div className="flex flex-col gap-2 mt-2" data-testid="board-comments">
                    {[...comments].reverse().map((c) => (
                      <div key={c.id} className="rounded-md border border-card-border px-2.5 py-1.5">
                        <div className="flex items-center gap-2 text-[11px] text-[var(--page-muted,#6b7280)]">
                          <span className="font-medium text-page-text inline-flex items-center gap-1">
                            {c.author_kind !== "user" && <Bot size={11} className="shrink-0" />}
                            {c.author_kind === "user"
                              ? (c.author?.name || c.author?.email || "user")
                              : (c.author_label || c.author_kind)}
                          </span>
                          <span title={c.created_at}>{timeAgo(c.created_at)}</span>
                        </div>
                        <div className="text-xs text-page-text whitespace-pre-wrap break-words mt-0.5">{linkifyText(c.body)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {report && (
                <div>
                  <div className={sectionLabel + " mb-1"}>Report</div>
                  <div
                    data-testid="board-report-md"
                    className={"text-xs text-page-text break-words bg-[var(--table-stripe,#f3f4f6)] rounded-md p-3 relative " + (!showFullReport && report.length > 1500 ? "max-h-64 overflow-hidden" : "")}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{report}</ReactMarkdown>
                    {!showFullReport && report.length > 1500 && (
                      <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[var(--table-stripe,#f3f4f6)] to-transparent rounded-b-md" />
                    )}
                  </div>
                  {report.length > 1500 && (
                    <button
                      type="button"
                      data-testid="board-report-toggle"
                      onClick={() => setShowFullReport((v) => !v)}
                      className="mt-1.5 text-xs text-nav-active bg-transparent border-0 p-0 cursor-pointer hover:underline"
                    >
                      {showFullReport ? "Show less" : "Show full report"}
                    </button>
                  )}
                </div>
              )}
              {orderedLog.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={sectionLabel}>Activity</span>
                    {/* Agent effort, folded into one chip — a 6-row grid for
                        three numbers was the window's most wasteful section.
                        Full breakdown lives in the tooltip. */}
                    {r.result?.metrics && (
                      <span
                        className="inline-flex items-center gap-1 text-[11px] text-[var(--page-muted,#6b7280)]"
                        title={`Agent effort — LLM ${fmtMs(r.result.metrics.llm_ms || r.result.metrics.wall_ms || 0)} · tokens in/out ${fmtTokens(r.result.metrics.input_tokens || 0)}/${fmtTokens(r.result.metrics.output_tokens || 0)} · cache read ${fmtTokens(r.result.metrics.cache_read_tokens || 0)} · turns ${r.result.metrics.turns || 0}`}
                      >
                        <Zap size={11} className="shrink-0" />
                        {fmtMs(r.result.metrics.llm_ms || r.result.metrics.wall_ms || 0)} LLM · {fmtTokens((r.result.metrics.input_tokens || 0) + (r.result.metrics.cache_creation_tokens || 0))} tok
                        {r.result.metrics.cost_usd > 0 ? ` · $${Number(r.result.metrics.cost_usd).toFixed(2)}` : ""}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {(showAllLog ? orderedLog : orderedLog.slice(-3)).map((e, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <span className="text-[var(--page-muted,#6b7280)] shrink-0 w-32" title={e.at}>{logStamp(e.at)}</span>
                        <span className="text-page-text break-words min-w-0">{e.msg}</span>
                      </div>
                    ))}
                  </div>
                  {orderedLog.length > 3 && (
                    <button
                      type="button"
                      data-testid="board-activity-toggle"
                      onClick={() => setShowAllLog((v) => !v)}
                      className="mt-1.5 text-xs text-nav-active bg-transparent border-0 p-0 cursor-pointer hover:underline"
                    >
                      {showAllLog ? "Show less" : `Show all (${orderedLog.length})`}
                    </button>
                  )}
                </div>
              )}
              {/* Task links, PASSIVE display only — authoring (attach/detach/
                  add-subtask) is retired from the UI, but agent-filed
                  follow-ups arrive parent-linked and must not look like they
                  appeared out of nowhere. Hidden entirely when no links. */}
              {(r.parent_request_id != null || childTasks.length > 0) && (
                <div>
                  <div className={sectionLabel + " mb-1"}>Linked tasks</div>
                  {r.parent_request_id != null && (
                    <div className="flex items-center gap-2 text-sm">
                      <CornerDownRight size={13} className="text-[var(--page-muted,#6b7280)] shrink-0" />
                      <button
                        type="button"
                        onClick={() => onOpenTask(r.parent_request_id)}
                        className="text-nav-active hover:underline bg-transparent border-0 cursor-pointer p-0 truncate"
                      >
                        #{r.parent_request_id}{parent ? ` ${parent.title}` : ""}
                      </button>
                      {parent && <StatusChip status={parent.status} />}
                    </div>
                  )}
                  {childTasks.length > 0 && (
                    <div className="mt-1 flex flex-col gap-1">
                      {childTasks.map((t) => (
                        <div key={t.id} className="flex items-center gap-2 text-sm min-w-0">
                          <Link2 size={12} className="text-[var(--page-muted,#6b7280)] shrink-0" />
                          <button
                            type="button"
                            onClick={() => onOpenTask(t.id)}
                            className="text-nav-active hover:underline bg-transparent border-0 cursor-pointer p-0 truncate"
                          >
                            #{t.id} {t.title}
                          </button>
                          <StatusChip status={t.status} queuePosition={t.queue_position} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Task GRAPH edges (app_request_link) — typed, directed, either
                  direction, and authorable here: this is the board's answer to
                  the Docs vault's [[wikilinks]]. Distinct from the parent tree
                  above, which stays passive. */}
              <TaskLinks id={r.id} onOpenTask={onOpenTask} />
            </div>
            {/* Footer: task-level actions left; Save right. */}
            <div className="px-6 py-3 border-t border-card-border flex items-center gap-2">
              {r.status === "done" && url && (
                <Link to={url} className={btnPrimary + " no-underline"} onClick={onClose}>
                  <AppWindow size={14} /> Open
                </Link>
              )}
              {/* Stopping a LIVE agent also lives HERE, not only on the card's
                  hover Cancel. This window is where you watch a run — the
                  Activity trail ticking, the token count climbing — so it is
                  where the decision to kill it is actually made; hover-on-card
                  is only discoverable if you already know it exists.
                  onCancel takes the id and re-resolves the row itself, so this
                  always targets THIS window's task (r.id), never the last card
                  hovered. It confirms before killing. Parked cards keep their
                  card-level Cancel: reversible, and not worth a second home.
                  Resuming a waiting card = drag it to Queued (or the chat
                  agent's board_requeue_task). */}
              {(r.status === "claimed" || r.status === "running") && onCancel && (
                <button
                  type="button"
                  disabled={busy}
                  data-testid={`task-stop-${r.id}`}
                  title="Stop the agent and cancel this task"
                  className={btnGhost + " text-[var(--severity-warning-text,#92400e)]"}
                  onClick={() => onCancel(r.id)}
                >
                  <X size={14} /> Stop
                </button>
              )}
              {terminal && !r.dismissed_at && (
                <button type="button" disabled={busy} className={btnGhost} onClick={() => { onDismiss(r.id); onClose(); }}>
                  <X size={14} /> Remove from board
                </button>
              )}
              <span className="flex-1" />
              {editable ? (
                <>
                  <button type="button" disabled={saving} className={btnGhost} onClick={requestClose}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    data-testid="board-edit-save"
                    disabled={saving || (!dirty && !commentText.trim())}
                    onClick={saveEdit}
                    className="inline-flex items-center gap-1.5 rounded-md bg-nav-active text-white text-sm px-4 py-1.5 border-0 cursor-pointer hover:bg-nav-active/90 disabled:opacity-50"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
                  </button>
                </>
              ) : (
                <button type="button" className={btnGhost} onClick={onClose}>Close</button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Board() {
  // ONE type vocabulary for both UI modes (a Standard-mode trim used to live
  // here and silently hid kinds, e.g. new_project).
  const taskKinds = TASK_KINDS;
  const [requests, setRequests] = useState(null); // null = loading, [] = empty
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [showCleared, setShowCleared] = useState(false);
  const [dragReq, setDragReq] = useState(null); // { id, from }
  const [dropCol, setDropCol] = useState(null); // column key under the drag
  const [detailId, setDetailId] = useState(null);
  // Card 💬 chip opens the window landed on the thread, composer ready.
  const [focusComments, setFocusComments] = useState(false);
  const [taskDialog, setTaskDialog] = useState(null); // {} | { presetParent, presetProject } | null
  const { projects, projectSlug: currentProjectSlug } = useProject();
  // Project filter. Defaults to the project you are in, so the board opens on
  // the work at hand instead of every project's cards at once; "" = all
  // projects you can see. Server-side (?project=), applied on top of the
  // access scoping — it narrows, it never widens.
  const [projectFilter, setProjectFilter] = useState(currentProjectSlug || "");
  // Follow the app's project switcher unless the user has overridden the
  // filter by hand this session.
  const filterTouched = useRef(false);
  useEffect(() => {
    if (!filterTouched.current) setProjectFilter(currentProjectSlug || "");
  }, [currentProjectSlug]);
  const { user } = useAuth(); // whose board this is — teammates' cards get a requester chip
  const { confirm } = useConfirm(); // Stop on a LIVE agent asks first

  // Change-targets for the New task dialog, loaded for whichever project is
  // selected in it (cross-project — the explicit header override wins).
  const loadTaskTargets = useCallback(async (kind, slug) => {
    if (kind === "change" || kind === "bug_fix") {
      const r = await listApps(slug ? { projectSlug: slug } : {});
      return (r?.apps || []).map((a) => ({ id: a.id, label: a.title || a.view_name }));
    }
    if (kind === "change_agent") {
      const r = await listAgents(slug ? { projectSlug: slug } : {});
      return (r?.agents || []).map((a) => ({ id: a.id, label: a.display_name || a.name }));
    }
    return [];
  }, []);

  const refresh = useCallback(async () => {
    try {
      // scope=team is project-ACCESS-scoped server-side: every task whose
      // project the caller can open, plus their own rows. No Mine toggle —
      // the board simply shows everything you are allowed to see.
      const res = await fetchMyRequests({
        scope: "team",
        ...(projectFilter ? { project: projectFilter } : {}),
        ...(showCleared ? { include_dismissed: 1 } : {}),
      });
      setRequests(res?.requests || []);
    } catch {
      /* silent — transient auth/network; the next poll retries */
    }
  }, [showCleared, projectFilter]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    // The alerts bell fires on the same completion events; refresh in step.
    const unsub = subscribeAlertsChange(refresh);
    return () => { clearInterval(id); unsub(); };
  }, [refresh]);

  // Tick the elapsed clocks on in-flight cards (local re-render, no fetch).
  const hasInFlight = (requests || []).some((r) => OPEN_STATUSES.has(r.status));
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!hasInFlight) return;
    const id = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(id);
  }, [hasInFlight]);

  const withBusy = useCallback(async (id, fn) => {
    setBusyIds((prev) => new Set(prev).add(id));
    try { await fn(); } finally {
      setBusyIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  }, []);

  const handleCancel = useCallback(async (id) => {
    // Stopping a LIVE agent is confirmed; cancelling a parked card is not
    // (it is reversible — cancelled cards can be dragged back to Queued).
    const row = (requests || []).find((r) => r.id === id);
    const inFlight = row && (row.status === "claimed" || row.status === "running");
    if (inFlight && !(await confirm({
      title: `Stop task #${id}?`,
      description: "The agent is still working. Stopping cancels the task and kills its run — partial changes may remain.",
      confirmText: "Stop",
      cancelText: "Keep running",
    }))) return;
    await withBusy(id, async () => {
      try { await cancelMyRequest(id); } catch (e) { toast.error(e?.message || "cancel failed"); }
      await refresh();
    });
  }, [withBusy, refresh, requests, confirm]);

  const handleDismiss = useCallback((id) => withBusy(id, async () => {
    setRequests((prev) => (prev || []).filter((r) => r.id !== id || showCleared));
    try { await dismissMyRequest(id); } catch { /* surfaced by refresh */ }
    await refresh();
  }), [withBusy, refresh, showCleared]);

  const handleClearHistory = useCallback(async () => {
    try { await dismissMyTerminalRequests(); } catch { /* surfaced by refresh */ }
    await refresh();
  }, [refresh]);

  // Optimistic drag-drop move; the backend re-checks `from` (409 on a race —
  // e.g. the drain claimed the card mid-drag), refresh reconciles either way.
  const handleMove = useCallback(async (req, to) => {
    const from = req.status;
    if (!BOARD_MOVES[from]?.has(to)) return;
    // Untyped captures (Feed → To Do) can't queue: the fleet dispatches
    // executors by kind. The server enforces this too — the pre-check just
    // gives a friendlier message than an optimistic move bouncing back.
    if (to === "queued" && req.kind === "triage") {
      toast.error("Set the task type first — the Set type button on the card.");
      return;
    }
    setRequests((prev) => (prev || []).map((r) => (r.id === req.id ? { ...r, status: to, queue_position: null } : r)));
    try {
      await moveRequest(req.id, from, to);
    } catch (e) {
      toast.error(e?.message || "move failed — the board was refreshed");
    }
    await refresh();
  }, [refresh]);

  const byColumn = useMemo(() => {
    const m = {};
    for (const col of BOARD_COLUMNS) m[col.key] = [];
    for (const r of requests || []) {
      const col = BOARD_COLUMNS.find((c) => c.statuses.includes(r.status));
      if (col) m[col.key].push(r);
    }
    return m;
  }, [requests]);

  // Task-link lookups over the already-loaded list: id → task, and
  // parent id → its attached tasks. No extra fetches.
  const byId = useMemo(() => {
    const m = new Map();
    for (const r of requests || []) m.set(r.id, r);
    return m;
  }, [requests]);
  const childrenOf = useMemo(() => {
    const m = new Map();
    for (const r of requests || []) {
      if (r.parent_request_id == null) continue;
      if (!m.has(r.parent_request_id)) m.set(r.parent_request_id, []);
      m.get(r.parent_request_id).push(r);
    }
    return m;
  }, [requests]);


  // Kind-aware: an untyped (triage) card can't queue, so Queued must not
  // glow as a drop target for it — the highlight is a promise the server
  // would break with a 400.
  const canDropIn = (col) =>
    !!(dragReq && col.drop && col.drop !== dragReq.from && BOARD_MOVES[dragReq.from]?.has(col.drop) &&
       !(col.drop === "queued" && dragReq.kind === "triage"));

  const columnHandlers = (col) => ({
    onDragOver: (e) => {
      if (!canDropIn(col)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (dropCol !== col.key) setDropCol(col.key);
    },
    onDragLeave: () => { setDropCol((c) => (c === col.key ? null : c)); },
    onDrop: (e) => {
      e.preventDefault();
      setDropCol(null);
      if (!canDropIn(col)) return;
      const req = (requests || []).find((r) => r.id === dragReq.id);
      setDragReq(null);
      if (req) handleMove(req, col.drop);
    },
  });

  const detail = detailId != null ? (requests || []).find((r) => r.id === detailId) || null : null;
  const hasTerminal = (requests || []).some((r) => TERMINAL_STATUSES.has(r.status));

  // Tasks the open detail may be attached to: everything of the user's except
  // itself and its own descendants (client-side mirror of the server's cycle
  // guard, so the select never offers an invalid target).
  const attachCandidates = useMemo(() => {
    if (!detail) return [];
    const descendsFromDetail = (t) => {
      let cur = t;
      for (let d = 0; cur && d < 25; d++) {
        if (cur.id === detail.id) return true;
        cur = cur.parent_request_id != null ? byId.get(cur.parent_request_id) : null;
      }
      return false;
    };
    return (requests || []).filter((t) => !descendsFromDetail(t));
  }, [detail, requests, byId]);

  return (
    <div className="h-full min-h-0 flex flex-col bg-page-bg pt-[30px] px-4 md:px-[60px] pb-4 gap-4">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-[32px] font-medium leading-tight text-page-title flex items-center gap-3">
            <ClipboardList size={28} className="text-nav-active" /> Board
          </h1>
          <p className="text-sm text-page-muted mt-1">
            Tasks, builds, and investigations{projectFilter ? ` in ${projectFilter}` : " across all projects"}. Drag a task to Queued to hand it to an agent, or to Done to close it out yourself; drag a failed or cancelled card back to retry.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {requests === null && <Loader2 size={15} className="animate-spin text-page-muted" />}
          {/* Project filter — defaults to the project you are in. Server-side,
              so a big board stays fast; "All projects" restores the old view. */}
          <select
            data-testid="board-project-filter"
            value={projectFilter}
            onChange={(e) => { filterTouched.current = true; setProjectFilter(e.target.value); }}
            className="h-8 rounded-md border border-card-border bg-card-bg px-2 text-sm text-page-title"
            title="Show only tasks of one project"
          >
            <option value="">All projects</option>
            {(projects || []).map((p) => (
              <option key={p.slug} value={p.slug}>{p.name || p.slug}</option>
            ))}
          </select>
          <button
            type="button"
            data-testid="my-builds-show-cleared"
            onClick={() => setShowCleared((v) => !v)}
            className={btnGhost + (showCleared ? " text-nav-active" : "")}
            title="Cleared builds are hidden, never deleted — toggle to browse the full history"
          >
            <History size={14} /> {showCleared ? "Hide cleared" : "Show cleared"}
          </button>
          {hasTerminal && (
            <button type="button" className={btnGhost} onClick={handleClearHistory} title="Remove all finished builds from the board">
              <Trash2 size={14} /> Clear history
            </button>
          )}
          <button type="button" data-testid="board-new-task" className={btnPrimary} onClick={() => setTaskDialog({})}>
            <Plus size={14} /> New task
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex gap-3 overflow-x-auto items-stretch pb-2">
        {BOARD_COLUMNS.map((col) => {
          const items = byColumn[col.key] || [];
          const highlight = dropCol === col.key && canDropIn(col);
          return (
            <div
              key={col.key}
              data-testid={`board-column-${col.key}`}
              {...columnHandlers(col)}
              className={
                // Fluid columns: share the full width on wide monitors (the
                // whole board stays in frame); min-width kicks in below
                // ~1620px and the container's overflow-x-auto takes over as
                // the honest fallback — fixed 300px columns needed ~2000px
                // and read as "cut off" on ordinary screens.
                "flex flex-col flex-1 min-w-[250px] min-h-0 rounded-xl border bg-card-bg/40 transition-colors " +
                (highlight ? "border-nav-active ring-1 ring-nav-active bg-nav-active/5" : "border-card-border")
              }
            >
              <div className="px-3 py-2.5 flex items-center gap-2 border-b border-card-border">
                <span className="text-sm font-semibold text-page-text">{col.title}</span>
                <span className="text-xs text-page-muted">{items.length}</span>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-2 flex flex-col gap-2">
                {items.map((r) => (
                  <Card
                    key={r.id}
                    r={r}
                    foreign={r.requester?.email && user?.email && r.requester.email.toLowerCase() !== user.email.toLowerCase() ? r.requester : null}
                    parent={r.parent_request_id != null ? byId.get(r.parent_request_id) : null}
                    childCount={(childrenOf.get(r.id) || []).length}
                    onOpenTask={(id) => { setFocusComments(false); setDetailId(id); }}
                    busy={busyIds.has(r.id)}
                    dragging={dragReq?.id === r.id}
                    draggable={!!BOARD_MOVES[r.status]}
                    onDragStart={(e) => {
                      setDragReq({ id: r.id, from: r.status, kind: r.kind });
                      try { e.dataTransfer.setData(DND_TYPE, String(r.id)); e.dataTransfer.effectAllowed = "move"; } catch {}
                    }}
                    onDragEnd={() => { setDragReq(null); setDropCol(null); }}
                    onOpen={() => { setFocusComments(false); setDetailId(r.id); }}
                    onOpenComments={(id) => { setFocusComments(true); setDetailId(id); }}
                    onCancel={handleCancel}
                    onDismiss={handleDismiss}
                    onSetType={(id) => { setFocusComments(false); setDetailId(id); }}
                  />
                ))}
                {items.length === 0 && (
                  <div className="text-xs text-page-muted text-center py-6 px-2">
                    {col.key === "todo" ? "Park tasks here — nothing runs until you drag one to Queued." : "Empty"}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <TaskModal
        r={detail}
        parent={detail?.parent_request_id != null ? byId.get(detail.parent_request_id) : null}
        childTasks={detail ? childrenOf.get(detail.id) || [] : []}
        onOpenTask={(id) => { setFocusComments(false); setDetailId(id); }}
        busy={detail ? busyIds.has(detail.id) : false}
        onClose={() => { setDetailId(null); setFocusComments(false); }}
        focusComments={focusComments}
        onDismiss={handleDismiss}
        onCancel={handleCancel}
        onEdited={refresh}
        taskKinds={taskKinds}
        loadTargets={loadTaskTargets}
      />

      {taskDialog && (
        <AppRequestDialog
          kinds={taskKinds}
          title="New task"
          initialStatus="todo"
          kindLayout="select"
          size="large"
          projects={projects}
          defaultProject={taskDialog.presetProject || currentProjectSlug}
          loadTargets={loadTaskTargets}
          submitLabel="Add to board"
          onClose={() => setTaskDialog(null)}
          onCreated={() => { setTaskDialog(null); refresh(); }}
        />
      )}
    </div>
  );
}
